import { useState, useRef, useEffect } from 'react';
import { X, Trash2 } from 'lucide-react';

const TILE_W = 200;
const TILE_H = 60;
// SAME canvas dimensions as Build mode so positions persist 1:1 across the transition.
const CANVAS_W = 1200;
const CANVAS_H = 700;

// Scatter within the CENTER 60% of the canvas. Avoid edges so users don't miss tiles.
function scatterPosition(existingPositions) {
  const insetX = CANVAS_W * 0.2;
  const insetY = CANVAS_H * 0.2;
  const minX = insetX;
  const maxX = CANVAS_W - insetX - TILE_W;
  const minY = insetY;
  const maxY = CANVAS_H - insetY - TILE_H;
  for (let attempt = 0; attempt < 80; attempt++) {
    const x = minX + Math.random() * Math.max(1, maxX - minX);
    const y = minY + Math.random() * Math.max(1, maxY - minY);
    const overlaps = existingPositions.some(
      (p) => Math.abs(p.x - x) < TILE_W + 10 && Math.abs(p.y - y) < TILE_H + 6
    );
    if (!overlaps) return { x, y };
  }
  return {
    x: minX + Math.random() * Math.max(1, maxX - minX),
    y: minY + Math.random() * Math.max(1, maxY - minY),
  };
}

// Animated demo: text types into the input box, then pops out as a pill. Repeats.
function CaptureDemo() {
  return (
    <div className="rounded-2xl border border-black/10 overflow-hidden bg-canvas/50 mt-2">
      <div className="relative px-4 py-4">
        {/* Mini input — text types in then disappears */}
        <div className="bg-white rounded-2xl border-2 border-black/15 px-3 py-2 text-sm w-52 mx-auto relative overflow-hidden">
          <span className="capture-input-text">Budget</span>
          <span className="capture-input-cursor">|</span>
        </div>
        {/* Arrow showing flow */}
        <div className="text-center text-txt-secondary/40 text-lg my-1">↓</div>
        {/* Pill pops out below */}
        <div className="flex justify-center">
          <div className="capture-output-pill bg-navy text-white rounded-2xl pill-glow border border-white/15 px-5 py-2 text-sm font-bold shadow-lg min-w-[100px] text-center">
            Budget
          </div>
        </div>
      </div>
      <p className="text-[11px] text-center text-txt-secondary pb-2.5 px-3 italic">
        Type a concept, hit Enter. It appears on the canvas. Repeat.
      </p>
    </div>
  );
}

export default function BrainstormMode({ tiles, cutTiles = [], onAddTile, onUpdateTile, onCutTile, onRestoreTile, onDeleteTile, onTransition }) {
  const [newText, setNewText] = useState('');
  const [showOverlay, setShowOverlay] = useState(false);
  const inputRef = useRef(null);
  const viewportRef = useRef(null);
  const [scale, setScale] = useState(1);

  // Fit the canvas ONCE when first visible. Disconnects after — resizing
  // the browser does NOT recompute (avoids the "concepts shift" issue).
  const didFit = useRef(false);
  useEffect(() => {
    if (!viewportRef.current) return;
    const el = viewportRef.current;
    const ro = new ResizeObserver((entries) => {
      if (didFit.current) return;
      const r = entries[0].contentRect;
      if (r.width === 0 || r.height === 0) return;
      didFit.current = true;
      const sW = r.width / (CANVAS_W + 40);
      const sH = r.height / (CANVAS_H + 40);
      const s = Math.max(0.55, Math.min(1.0, sW, sH));
      setScale(s);
      ro.disconnect();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // First-use overlay
  useEffect(() => {
    if (!localStorage.getItem('clarity_capture_seen')) {
      setShowOverlay(true);
    }
  }, []);

  useEffect(() => {
    if (inputRef.current && !showOverlay) inputRef.current.focus();
  }, [showOverlay]);

  useEffect(() => {
    if (!newText && inputRef.current && !showOverlay) inputRef.current.focus();
  }, [tiles.length, newText, showOverlay]);

  function handleAdd() {
    const trimmed = newText.trim();
    if (trimmed) {
      // Compute a real position in the SAME coordinate system Build uses, save it.
      // This way, positions persist 1:1 when transitioning to Build.
      const existing = tiles.map((t) => ({ x: t.posX || 0, y: t.posY || 0 }));
      const pos = scatterPosition(existing);
      onAddTile(trimmed, pos.x, pos.y);
      setNewText('');
    }
  }

  function dismissOverlay() {
    setShowOverlay(false);
    localStorage.setItem('clarity_capture_seen', 'true');
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* First-use overlay */}
      {showOverlay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={dismissOverlay} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg p-8">
            <p className="text-txt-primary text-base leading-relaxed">
              Name everything connected to this communication. Topics you must cover. Points you want to make. Things you are not sure belong but keep coming up. Questions they will probably ask. Anything rattling around. Just a word or two each. Think: Why. What. How. When. Who. How Much. What's Next.
            </p>
            <p className="text-txt-primary text-base leading-relaxed mt-4">
              Do not filter. Do not explain. Spelling is not important. Just get it out.
            </p>
            <p className="text-txt-secondary text-base leading-relaxed mt-4 italic">
              When you start running out of things to add, you are ready to move on.
            </p>
            <button onClick={dismissOverlay} className="btn-gold mt-6 w-full text-center font-heading uppercase">
              Got It
            </button>
          </div>
        </div>
      )}

      {/* Bar — step label + count on left, Add Concept + That's All I Got centered together */}
      <div className="flex-shrink-0 bg-white border-b border-black/10 px-4 py-3 grid grid-cols-3 items-center gap-3">
        {/* Left: step label + concept count */}
        <div className="justify-self-start flex items-center gap-3">
          <span className="text-sm text-black whitespace-nowrap font-heading uppercase font-bold tracking-wider bg-gold/30 border-2 border-gold-dark/50 rounded-lg px-3 py-1.5">
            Step 1 of 6 · Capture
          </span>
          {tiles.length > 0 && (
            <span className="text-xs text-txt-secondary whitespace-nowrap font-heading uppercase tracking-wide">
              {tiles.length} {tiles.length === 1 ? 'concept' : 'concepts'}
            </span>
          )}
        </div>

        {/* Center: input + Add Concept + That's All I Got — all same height, centered */}
        <div className="justify-self-center flex gap-2 items-center">
          <input
            ref={inputRef}
            type="text"
            value={newText}
            maxLength={80}
            onChange={(e) => setNewText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
            placeholder="Name a concept. Hit Enter."
            className="rounded-lg border-2 border-black/15 px-4 h-11 text-base w-72
              focus:border-gold focus:ring-2 focus:ring-gold/40 placeholder:text-gray-400
              bg-white"
          />
          <button
            onClick={handleAdd}
            disabled={!newText.trim()}
            className="btn-gold h-11 px-5 rounded-lg text-base font-heading uppercase tracking-wide shadow-md
              disabled:opacity-30 flex items-center"
          >
            Add Concept
          </button>
          <button
            onClick={onTransition}
            disabled={tiles.length === 0}
            className="btn-gold h-11 px-5 rounded-lg shadow-lg flex items-center
              disabled:opacity-30 disabled:cursor-not-allowed font-heading font-bold uppercase text-base whitespace-nowrap"
          >
            That's All I Got
          </button>
        </div>

        {/* Right: spacer for grid balance */}
        <div className="justify-self-end" />
      </div>

      {/* Scattered canvas — fits entire 1200x700 area in the viewport via scale transform */}
      <div ref={viewportRef} className="flex-1 relative bg-canvas overflow-hidden flex items-center justify-center">
        <div
          className="relative"
          style={{
            width: CANVAS_W,
            height: CANVAS_H,
            transform: `scale(${scale})`,
            transformOrigin: 'center center',
          }}
        >
          {/* Watermark */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <img src="/bcd-icon.png" alt="" className="w-48 opacity-[0.03]" />
          </div>

          {tiles.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-8">
              <div className="bg-white/80 rounded-2xl shadow-lg border border-black/10 px-10 py-8 max-w-xl">
                <h2 className="text-xl font-heading font-bold uppercase tracking-wide text-black mb-5 text-center">
                  Empty Your Head. Fill the Page.
                </h2>
                <p className="text-base text-txt-primary leading-relaxed mb-4">
                  See the box that says <strong>"Name a concept"</strong> above? That is where, one at a time, you list the things you think should be in this communication. Empty your head until its all on the page.
                </p>
                <p className="text-base text-txt-primary leading-relaxed mb-4">
                  Then click <strong className="text-black">THAT'S ALL I GOT</strong>. When you do, Clarity begins.
                </p>
                {/* Animated demo: pills appear one by one */}
                <CaptureDemo />
              </div>
            </div>
          )}

          {tiles.map((tile) => (
            <BrainstormTile
              key={tile.id}
              tile={tile}
              x={tile.posX || 0}
              y={tile.posY || 0}
              scale={scale}
              onUpdate={onUpdateTile}
              onCut={onCutTile}
            />
          ))}
        </div>
      </div>

      {/* Removed Concepts bar — same as Build */}
      <CutBar tiles={cutTiles} onRestore={onRestoreTile} onDelete={onDeleteTile} />
    </div>
  );
}

function CutBar({ tiles, onRestore, onDelete }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex-shrink-0 bg-cutcharcoal text-white z-20 border-t-2 border-brand-red/60">
      <button onClick={() => setOpen(!open)}
        className="w-full px-4 py-2.5 flex items-center justify-between text-sm hover:bg-gray-700 transition-colors relative z-10">
        <span className="flex items-center gap-2">
          <span className="font-heading font-bold uppercase tracking-wider text-xs">Removed Concepts</span>
          {tiles.length > 0 && (
            <span className="bg-brand-red text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-bold">{tiles.length}</span>
          )}
          {tiles.length === 0 && (
            <span className="text-gray-500 text-xs">none</span>
          )}
        </span>
        <span className="text-gray-400 text-xs">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && tiles.length > 0 && (
        <div className="px-4 py-3 flex flex-wrap gap-2 border-t border-gray-700">
          {tiles.map((tile) => (
            <div key={tile.id} className="bg-gray-600 text-gray-300 rounded-lg px-3 py-2 text-xs flex items-center gap-2 max-w-[220px]">
              <span className="truncate flex-1">{tile.text}</span>
              <button onClick={() => onRestore(tile.id)} className="text-green-400 hover:text-green-300 shrink-0 text-sm" title="Restore">↩</button>
              <button onClick={() => onDelete(tile.id)} className="text-red-400 hover:text-red-300 shrink-0" title="Delete permanently">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BrainstormTile({ tile, x, y, scale = 1, onUpdate, onCut }) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(tile.text);
  const [hovered, setHovered] = useState(false);
  const [dragging, setDragging] = useState(false);
  // Local drag position used during a drag, persisted to DB on release.
  const [localPos, setLocalPos] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  function saveText() {
    setEditing(false);
    const trimmed = editText.trim();
    if (trimmed && trimmed !== tile.text) {
      onUpdate(tile.id, { text: trimmed });
    }
  }

  const dragRef = useRef(null);
  const elRef = useRef(null);

  function handleMouseDown(e) {
    if (editing || e.button !== 0) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: x, origY: y, moved: false, lastX: x, lastY: y };

    function onMouseMove(ev) {
      if (!dragRef.current) return;
      // Divide screen-space deltas by scale so dragging stays 1:1 with the cursor at any zoom.
      const dx = (ev.clientX - dragRef.current.startX) / scale;
      const dy = (ev.clientY - dragRef.current.startY) / scale;
      if (!dragRef.current.moved && Math.abs(dx) + Math.abs(dy) < 4) return;
      dragRef.current.moved = true;
      setDragging(true);
      const nx = dragRef.current.origX + dx;
      const ny = dragRef.current.origY + dy;
      dragRef.current.lastX = nx;
      dragRef.current.lastY = ny;
      setLocalPos({ x: nx, y: ny });
    }

    function onMouseUp() {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      const moved = dragRef.current?.moved;
      const finalX = dragRef.current?.lastX;
      const finalY = dragRef.current?.lastY;
      dragRef.current = null;
      setDragging(false);
      if (moved && (finalX !== undefined)) {
        // Persist the new position to the DB so it's preserved across views.
        onUpdate(tile.id, { posX: finalX, posY: finalY });
      }
      setLocalPos(null);
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  const renderX = localPos ? localPos.x : x;
  const renderY = localPos ? localPos.y : y;

  return (
    <div
      ref={elRef}
      className="absolute select-none"
      style={{ left: renderX, top: renderY, zIndex: dragging ? 50 : 10 }}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className={`bg-navy rounded-2xl pill-glow border border-white/15 px-6 py-3.5
          max-w-[280px] min-w-[140px] relative hover:scale-[1.02] transition-all
          ${dragging ? 'cursor-grabbing scale-[1.04]' : 'cursor-grab'}`}
        onDoubleClick={() => { setEditText(tile.text); setEditing(true); }}
      >
        {editing ? (
          <input
            ref={inputRef}
            type="text"
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onBlur={saveText}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); saveText(); }
              if (e.key === 'Escape') { setEditing(false); setEditText(tile.text); }
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className="w-full bg-transparent border-none outline-none p-0 text-base text-white font-semibold focus:ring-0"
          />
        ) : (
          <span className="text-base text-white font-semibold break-words">{tile.text}</span>
        )}

        {hovered && !editing && !dragging && (
          <button
            onClick={() => onCut(tile.id)}
            onMouseDown={(e) => e.stopPropagation()}
            className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-white shadow-md
              border-2 border-black/20 text-txt-secondary hover:text-red-500 hover:border-red-300
              flex items-center justify-center transition-colors z-20"
            title="Remove"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
